import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Tv, Wifi } from 'lucide-react';
import { customerAPI } from '../../services/api';

// Ctrl/Cmd+K: jump to any screen the user may open (`leaves` comes from the layout, so it
// follows the same permission rules as the menu) or to a customer by name/mobile/account.
// Presentation only — it just navigates; nothing here writes data.
const ACTIONS = [{ to: '/customers/add', label: 'Add customer', icon: UserPlus }];

export default function CommandPalette({ leaves }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [cursor, setCursor] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(o => !o); }
      else if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true); // the phone's top-bar button fires this (no Ctrl key on a phone)
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('open-command-palette', onOpen); };
  }, []);

  useEffect(() => { if (!open) { setQ(''); setPeople([]); setCursor(0); } }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    const term = q.trim();
    if (!open || term.length < 2) { setPeople([]); return undefined; }
    timer.current = setTimeout(() => {
      customerAPI.search(term, 6).then(setPeople).catch(() => setPeople([]));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (r) => !term || r.label.toLowerCase().includes(term);
    return [
      ...ACTIONS.filter(match).map(r => ({ ...r, group: 'Quick actions' })),
      ...people.map(c => ({
        to: `/customers/${c._id}`, label: c.name, icon: c.customerType === 'catv' ? Tv : Wifi,
        hint: [c.customerId, c.mobile].filter(Boolean).join(' · '), group: 'Customers',
      })),
      ...leaves.filter(match).slice(0, 12).map(r => ({ ...r, group: 'Go to' })),
    ];
  }, [q, people, leaves]);

  if (!open) return null;

  const go = (r) => { setOpen(false); navigate(r.to); };
  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && rows[cursor]) go(rows[cursor]);
  };

  return (
    <div onMouseDown={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '12vh' }}>
      <div onMouseDown={e => e.stopPropagation()} className="ui-card"
        style={{ width: 'min(560px, 94vw)', padding: 0, overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '0 var(--space-4)', borderBottom: '1px solid var(--border)' }}>
          <Search size={16} color="var(--text-muted)" />
          <input autoFocus value={q} onChange={e => { setQ(e.target.value); setCursor(0); }} onKeyDown={onInputKey}
            placeholder="Go to a page, or find a customer…"
            style={{ flex: 1, height: 'var(--tap)', border: 'none', outline: 'none', background: 'transparent', font: 'inherit', fontSize: '16px', color: 'var(--text)' }} />
          <kbd style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>Esc</kbd>
        </div>
        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: 'var(--space-1) 0' }}>
          {rows.length === 0 && <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--fs-md)' }}>Nothing found</div>}
          {rows.map((r, i) => (
            <React.Fragment key={`${r.group}-${r.to}`}>
              {(i === 0 || rows[i - 1].group !== r.group) && (
                <div style={{ padding: 'var(--space-2) var(--space-4) var(--space-1)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', fontWeight: 600 }}>{r.group}</div>
              )}
              <div onMouseDown={() => go(r)} onMouseEnter={() => setCursor(i)}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minHeight: 'var(--tap)', padding: '0 var(--space-4)', cursor: 'pointer', background: i === cursor ? 'var(--primary-light)' : 'transparent' }}>
                <r.icon size={16} color="var(--text-muted)" />
                <span style={{ fontSize: 'var(--fs-md)', flex: 1 }}>{r.label}</span>
                {r.hint && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{r.hint}</span>}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
